import SelectDialog from "sap/m/SelectDialog";
import StandardListItem from "sap/m/StandardListItem";
import BindingMode from "sap/ui/model/BindingMode";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import JSONModel from "sap/ui/model/json/JSONModel";
import type Event from "sap/ui/base/Event";
import type ListBinding from "sap/ui/model/ListBinding";

/**
 * @namespace com.ang.portal.util
 */

const MODEL_NAME = "valueHelp";

/** Every field the search field matches against, case-insensitively. */
const SEARCHABLE_PATHS = ["key", "text", "description"];

export interface ValueHelpEntry {
	key: string;
	text: string;
	description?: string;
}

interface ValueHelpRow extends ValueHelpEntry {
	selected: boolean;
}

export interface MultiSelectValueHelpOptions {
	title: string;
	entries: ValueHelpEntry[];
	/** Keys already tokenized in the input; those rows open pre-selected. */
	selectedKeys: Iterable<string>;
	onConfirm: (keys: string[]) => void;
}

function buildSearchFilter(query: string): Filter[] {
	const value = query.trim();
	if (!value) {
		return [];
	}

	return [
		new Filter({
			and: false,
			filters: SEARCHABLE_PATHS.map(
				(path) =>
					new Filter({
						path,
						operator: FilterOperator.Contains,
						value1: value,
					}),
			),
		}),
	];
}

export default {
	/**
	 * Opens a multi-select value help whose search field actually filters.
	 *
	 * `sap.m.SelectDialog` never filters its own list — the search field only
	 * fires `search`/`liveChange` and the application is expected to react. Built
	 * inline from a static `items` array, as these dialogs used to be, the box
	 * looks live but does nothing.
	 *
	 * So the list is bound to a client model and the search filters that binding,
	 * which leaves the context paths (and with them the list's own selection
	 * bookkeeping) intact. `selected` is bound two-way, so a row the current
	 * filter hides keeps its checkmark and still comes back in the confirmed keys
	 * — the list itself only reports the items it currently shows.
	 */
	openMultiSelect(options: MultiSelectValueHelpOptions): SelectDialog {
		const preselected = new Set(options.selectedKeys);
		const rows: ValueHelpRow[] = options.entries.map((entry) => ({
			...entry,
			selected: preselected.has(entry.key),
		}));

		const model = new JSONModel({ items: rows });
		model.setDefaultBindingMode(BindingMode.TwoWay);

		const applySearch = (oEvent: Event): void => {
			const binding = ((oEvent.getParameter("itemsBinding" as never) as
				| ListBinding
				| undefined) ?? oDialog.getBinding("items")) as ListBinding | undefined;
			if (!binding) {
				return;
			}

			const value =
				(oEvent.getParameter("value" as never) as string | undefined) || "";
			binding.filter(buildSearchFilter(value));
		};

		// One dialog per request, so it has to clean up after itself. `confirm` and
		// `cancel` both fire from the inner dialog's afterClose, and SelectDialog
		// guards its own post-event cleanup with `bIsDestroyed`.
		const oDialog = new SelectDialog({
			title: options.title,
			multiSelect: true,
			contentHeight: "20rem",
			search: applySearch,
			liveChange: applySearch,
			confirm: () => {
				const selected = (model.getProperty("/items") as ValueHelpRow[]).filter(
					(row) => row.selected,
				);
				options.onConfirm(selected.map((row) => row.key));
				oDialog.destroy();
			},
			cancel: () => {
				oDialog.destroy();
			},
		});

		const oTemplate = new StandardListItem({
			title: "{valueHelp>text}",
			description: "{valueHelp>description}",
		});
		oTemplate.bindProperty("selected", { path: "selected", model: MODEL_NAME });

		oDialog.setModel(model, MODEL_NAME);
		oDialog.bindAggregation("items", {
			path: `${MODEL_NAME}>/items`,
			template: oTemplate,
		});
		oDialog.open("");

		return oDialog;
	},
};
